import moment, { Moment } from "moment";
import { getToken } from "./storageUtilities";
import jwt from "jwt-decode";
import i18n from "i18n";
import {
    ComplianceScore,
    ComplianceTypes,
    DeviceType,
    HASURA_ROLE,
    OperativeSystem,
    defaultCountryCode,
} from "constants/constants";
import { IShift } from "pages/Corporate/Locations/MillDashboards/LiveDowntimeReport/LiveDowntimeReportAPIInterfaces";
import { IShift as IShiftMill } from "entities/Mills/MillsEntityInterfaces";
import { DateRange, extendMoment } from "moment-range";
import {
    IAPComplianceByResponsible,
    IRCAComplianceByResponsible,
} from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntityInterfaces";
import { IShiftOptions } from "pages/Corporate/CorporateSetupDashboard/Corporations/MillSettingsStep/ShiftsInput/components/ShiftDeletionForm/ShiftDeletionFormInterfaces";

const momentRange = extendMoment(moment as any);

export const truncateLongText = (input: string, limitLength: number): string => {
    return input && input.length > limitLength ? `${input.substring(0, limitLength)}…` : input;
};

export const calculateGraphTwoDigitPercentage = (value: number, total: number): number => {
    return Math.round((100 * value) / total);
};

export const calculateTrailingTrend = (currentValue: number, trailingValue: number): number => {
    return trailingValue ? Math.round(((currentValue - trailingValue) / trailingValue) * 100) : 0;
};

export const getDowntimeDurationInHoursMinutesSeconds = (durationInSeconds: number) => {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);

    const formattedDuration = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
        seconds,
    ).padStart(2, "0")}`;

    return formattedDuration;
};

export const getDowntimeDurationInHoursMinutes = (durationInSeconds: number, showSeconds: boolean = false) => {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);

    const adjustedMinutes = minutes < 1 && !showSeconds ? 1 : minutes;

    if (showSeconds) {
        return `${String(hours).padStart(2, "0")}:${String(adjustedMinutes).padStart(2, "0")}:${String(
            seconds,
        ).padStart(2, "0")}`;
    } else {
        return `${String(hours).padStart(2, "0")}:${String(adjustedMinutes).padStart(2, "0")}`;
    }
};

export const getToday = (): string => {
    return new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    });
};

export const getDataLastUpdatedDate = (date: Date): string => {
    const lastUpdated: Moment = moment.utc(date).local();
    return lastUpdated.format("lll");
};

export const getCurrentTime = (): string => {
    const now = new Date();
    const currentHours = now.getHours().toString().padStart(2, "0");
    const currentMinutes = now.getMinutes().toString().padStart(2, "0");
    const currentSeconds = now.getSeconds().toString().padStart(2, "0");

    return `${currentHours}:${currentMinutes}:${currentSeconds}`;
};

export const pickRandomColor = (): string => {
    const originalColorsArray = [
        "#C2E3F1",
        "#AEDAED",
        "#93CEE7",
        "#6FBDDF",
        "#3FA7D4",
        "#247BA0",
        "#1F6888",
        "#1A5974",
        "#164B62",
        "#134053",
        "#E0FFFF",
        "#BAE1E3",
        "#59C3C3",
        "#66A3A5",
        "#438C8F",
        "#007478",
        "#05696B",
        "#005A5C",
        "#004E4C",
        "#003F3C",
        "#FEFDF0",
        "#FDF8CE",
        "#FDF3A6",
        "#FFF082",
        "#FFED65",
        "#FFEA4B",
        "#FCD744",
        "#F9C13D",
        "#F5A936",
        "#EF8027",
        "#EAE0C5",
        "#EED9A3",
        "#F4D27B",
        "#FACD56",
        "#FDC834",
        "#FFC400",
        "#FBB106",
        "#F59A10",
        "#F77F00",
        "#E7521A",
        "#EFF6FE",
        "#CDE0FE",
        "#ACCDFE",
        "#8CB6FA",
        "#7CA4F6",
        "#7194F2",
        "#6C85E2",
        "#6774CF",
        "#5E63BC",
        "#52449C",
    ];

    let colorsArray: string[] = originalColorsArray.slice();

    const randomIndex: number = Math.floor(Math.random() * colorsArray.length);
    const randomColor: string = colorsArray[randomIndex];
    colorsArray.splice(randomIndex, 1);

    if (colorsArray.length === 0) {
        colorsArray = originalColorsArray.slice();
    }

    return randomColor;
};

export const getTimeTrends = (endDate: Date, repetition: number, timeUnitSpan: number): Date[] => {
    let date = endDate ? new Date(endDate) : new Date();
    date.setDate(date.getDate() - repetition * timeUnitSpan);

    const dates: Date[] = [];

    for (let d = date; d <= new Date(endDate); d.setDate(d.getDate() + timeUnitSpan)) {
        dates.push(new Date(d));
    }

    return dates;
};

export const isHMIToken = () => {
    const currentToken = getToken();
    if (currentToken) {
        try {
            // A malformed token in localStorage (truncated write, partially cleared storage) must
            // not throw during render: this runs inside ProtectedCorporateRoute, which is the
            // redirect target of the other guards, so an unhandled decode error white-screens the
            // app with no route out of it.
            const { hmi_token } = jwt(currentToken) as { hmi_token: boolean };
            if (hmi_token) {
                return true;
            }
        } catch {
            return false;
        }
    }
    return false;
};

interface IHasuraTokenClaims {
    "https://hasura.io/jwt/claims"?: {
        "x-hasura-default-role"?: string;
    };
}

export const getTokenDefaultRole = (): string | undefined => {
    const currentToken = getToken();
    if (!currentToken) {
        return undefined;
    }
    try {
        return (jwt(currentToken) as IHasuraTokenClaims)["https://hasura.io/jwt/claims"]?.["x-hasura-default-role"];
    } catch {
        return undefined;
    }
};

/**
 * The role is read from the signed token rather than CorporateContext.currentUser because the
 * route guards need it synchronously on first render: currentUser comes from a Hasura query that
 * has not resolved yet at guard time, and it never resolves at all when user_id is missing from
 * localStorage (GetCurrentUser returns [] and currentUser stays {}), so a "wait for it" guard
 * would hang. Client-side tampering gains nothing — Hasura enforces the same boundary.
 */
export const isApiAdministrator = (): boolean => getTokenDefaultRole() === HASURA_ROLE.API_ADMINISTRATOR;

export const formatBytes = (bytes: number, decimals = 2) => {
    if (!+bytes) return "0 Bytes";
    const baseNumber = 1024;
    const calculatedDecimals = decimals < 0 ? 0 : decimals;
    const fileSizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const roundedValue = Math.floor(Math.log(bytes) / Math.log(baseNumber));
    return `${parseFloat((bytes / Math.pow(baseNumber, roundedValue)).toFixed(calculatedDecimals))} ${
        fileSizes[roundedValue]
    }`;
};
// pSBC - Shade Blend Convert helper - Version 4.0
export function pSBC(p: number, c0: string, c1?: any, l?: any) {
    let r,
        g,
        b,
        P,
        f,
        t,
        h,
        i = parseInt,
        m = Math.round,
        a = typeof c1 === "string";
    if (
        typeof p !== "number" ||
        p < -1 ||
        p > 1 ||
        typeof c0 !== "string" ||
        (!c0.startsWith("r") && !c0.startsWith("#")) ||
        (c1 && !a)
    )
        return null;
    //@ts-ignore
    if (!window.pSBCr) {
        //@ts-ignore
        window.pSBCr = (d: any) => {
            let n = d.length,
                //@ts-ignore
                x: any = {};
            if (n > 9) {
                [r, g, b, a] = d = d.split(",");
                n = d.length;
                if (n < 3 || n > 4) return null;
                x.r = i(r[3] === "a" ? r.slice(5) : r.slice(4));
                x.g = i(g);
                x.b = i(b);
                //@ts-ignore
                x.a = a ? parseFloat(a) : -1;
            } else {
                if (n === 8 || n === 6 || n < 4) return null;
                if (n < 6) d = "#" + d[1] + d[1] + d[2] + d[2] + d[3] + d[3] + (n > 4 ? d[4] + d[4] : "");
                d = i(d.slice(1), 16);
                if (n === 9 || n === 5) {
                    x.r = (d >> 24) & 255;
                    x.g = (d >> 16) & 255;
                    x.b = (d >> 8) & 255;
                    x.a = m((d & 255) / 0.255) / 1000;
                } else {
                    x.r = d >> 16;
                    x.g = (d >> 8) & 255;
                    x.b = d & 255;
                    x.a = -1;
                }
            }
            return x;
        };
    }
    h = c0.length > 9;
    h = a ? (c1.length > 9 ? true : c1 === "c" ? !h : false) : h;
    //@ts-ignore
    f = window.pSBCr(c0);
    P = p < 0;
    //@ts-ignore
    t =
        c1 && c1 !== "c"
            ? //@ts-ignore
              window.pSBCr(c1)
            : P
              ? { r: 0, g: 0, b: 0, a: -1 }
              : { r: 255, g: 255, b: 255, a: -1 };
    p = P ? p * -1 : p;
    P = 1 - p;
    if (!f || !t) return null;
    if (l) {
        r = m(P * f.r + p * t.r);
        g = m(P * f.g + p * t.g);
        b = m(P * f.b + p * t.b);
    } else {
        r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5);
        g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5);
        b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5);
    }
    a = f.a;
    t = t.a;
    //@ts-ignore
    f = a >= 0 || t >= 0;
    //@ts-ignore
    a = f ? (a < 0 ? t : t < 0 ? a : a * P + t * p) : 0;
    //@ts-ignore
    if (h)
        return (
            "rgb" +
            (f ? "a(" : "(") +
            r +
            "," +
            g +
            "," +
            b +
            //@ts-ignore
            (f ? "," + m(a * 1000) / 1000 : "") +
            ")"
        );
    //@ts-ignore
    else
        return (
            "#" +
            //@ts-ignore
            (4294967296 + r * 16777216 + g * 65536 + b * 256 + (f ? m(a * 255) : 0))
                .toString(16)
                .slice(1, f ? undefined : -2)
        );
}

export function numberWithCommas(number: number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
export function formatNumberWithCommasAsThousandsSeparators(number: number) {
    return number
        .toFixed(0)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const getStartOfDateRange = (startDate: Date) => {
    const date = moment(startDate).format("YYYY-MM-DD");
    const start = moment.utc(date).startOf("day").toDate();

    return start;
};

export const getEndOfDateRange = (endDate: Date) => {
    const date = moment(endDate).format("YYYY-MM-DD");
    const end = moment.utc(date).endOf("day").toDate();

    return end;
};

export const getFirstDayOfTheMonthAtZeroHour = () => {
    let currentDate = new Date();
    currentDate.setDate(1);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
};

export const getTodayAtEndOfDay = () => {
    let currentDate = new Date();
    currentDate.setHours(23, 59, 59, 0);
    return currentDate;
};

export const percentageChecker = (percentage: number) => {
    return percentage > 100 ? 100 : percentage < 0 ? 0 : percentage;
};

export const isLate = (dateString: string): boolean => {
    const inputDate = new Date(dateString);
    const today = new Date();
    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return inputDate < today;
};

export const localeFormattedNumber = (countryCode: string | undefined, number: number | string): string | undefined => {
    const countryCodeString = countryCode || defaultCountryCode;
    const languageCode = i18n.language.slice(0, 2).toLowerCase();
    const localeParameter = `${languageCode}-${countryCodeString}`;
    return number.toLocaleString(localeParameter);
};

export const getDeviceOperativeSystem = () => {
    let userAgent = window.navigator.userAgent,
        platform = window.navigator.platform,
        macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
        windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
        iosPlatforms = ["iPhone", "iPad", "iPod"],
        os = null;

    if (macosPlatforms.indexOf(platform) !== -1) {
        os = OperativeSystem.MACOS;
    } else if (iosPlatforms.indexOf(platform) !== -1) {
        os = OperativeSystem.IOS;
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = OperativeSystem.WINDOWS;
    } else if (/Android/.test(userAgent)) {
        os = OperativeSystem.ANDROID;
    } else if (!os && /Linux/.test(platform)) {
        os = OperativeSystem.LINUX;
    }

    return os;
};

export const getDeviceType = () => {
    let userAgent = navigator.userAgent;

    let isMobile = /Android|webOS|iPhone/i.test(userAgent);
    let isTablet = /iPad|Tablet/i.test(userAgent);

    if (isMobile) {
        return DeviceType.MOBILE;
    } else if (isTablet) {
        return DeviceType.TABLET;
    } else {
        return DeviceType.DESKTOP;
    }
};

type ShiftType = IShiftOptions | IShiftMill;

export const checkShiftOverlapping = <T extends ShiftType>(
    shiftIdsArray: number[],
    shifts: T[],
    isProductionLineCreation: boolean,
): boolean => {
    let shiftsRanges: Array<DateRange[]> = [];

    shiftIdsArray.forEach((id) => {
        const selectedShift = shifts.find((shift: T) => shift.shift_id === id);

        const getDaysDetails = (shift: T) => {
            if (isProductionLineCreation) {
                return (shift as IShiftMill).shift_historic[0]?.shift_days?.filter((day) => day.is_enabled);
            } else {
                return (shift as IShiftOptions).shift_historic?.daysDetails;
            }
        };

        const getStartTime = (shift: T) => {
            return isProductionLineCreation
                ? (shift as IShiftMill).shift_historic[0]?.start_time
                : (shift as IShiftOptions).shift_historic?.start;
        };

        const getEndTime = (shift: T) => {
            return isProductionLineCreation
                ? (shift as IShiftMill).shift_historic[0]?.end_time
                : (shift as IShiftOptions).shift_historic?.end;
        };

        const millisecondsElapsedUntilStartTimeAndEndTimeSinceSundayByDay =
            getDaysDetails(selectedShift!)?.map((day) => {
                const millisecondsElapsedByDay = (day.day_id % 7) * 24 * 3600 * 1000;

                const millisecondsElapsedTodayUntilStartTime: number = moment(
                    getStartTime(selectedShift!),
                    "HH:mm:ss",
                ).valueOf();
                let millisecondsElapsedTodayUntilEndTime: number = moment(
                    getEndTime(selectedShift!),
                    "HH:mm:ss",
                ).valueOf();

                if (millisecondsElapsedTodayUntilStartTime > millisecondsElapsedTodayUntilEndTime) {
                    millisecondsElapsedTodayUntilEndTime += 24 * 60 * 60 * 1000;
                }

                const rangeStartTime = moment(millisecondsElapsedByDay + millisecondsElapsedTodayUntilStartTime);
                const rangeEndTime = moment(millisecondsElapsedByDay + millisecondsElapsedTodayUntilEndTime);

                return { rangeStartTime, rangeEndTime };
            }) || [];

        const momentRanges = millisecondsElapsedUntilStartTimeAndEndTimeSinceSundayByDay.map((millisecondsGroup) =>
            momentRange.range(millisecondsGroup.rangeStartTime, millisecondsGroup.rangeEndTime),
        );

        shiftsRanges = [...shiftsRanges, momentRanges];
    });

    let overlap = false;
    for (let i = 0; i < shiftsRanges.length - 1; i++) {
        const currentRanges = shiftsRanges[i];
        for (let j = i + 1; j < shiftsRanges.length; j++) {
            const nextRanges = shiftsRanges[j];
            for (const currentRange of currentRanges) {
                for (const nextRange of nextRanges) {
                    if (currentRange.overlaps(nextRange)) overlap = true;
                }
            }
        }
    }
    return overlap;
};

export const checkShiftOverlappingAtShiftEditing = (
    productionLineShifts: IShiftMill[],
): {
    singleShiftsArray: number[];
    overlap: boolean;
    shiftHoursByShiftId: {
        [key: number]: string[];
    }[];
} => {
    interface shiftsDataRanges {
        dateRanges: DateRange;
        shiftId: number;
        startTime: string;
        endTime: string;
    }
    let shiftsRanges: Array<shiftsDataRanges[]> = [];

    productionLineShifts.forEach((shift) => {
        const millisecondsElapsedUntilStartTimeAndEndTimeSinceSundayByDay =
            shift!.shift_historic[0]?.shift_days &&
            shift!.shift_historic[0]?.shift_days
                .filter((day) => day.is_enabled)
                .map((day) => {
                    const millisecondsElapsedByDay = (day.day_id % 7) * 24 * 3600 * 1000;

                    const millisecondsElapsedTodayUntilStartTime: number = moment(
                        shift?.shift_historic[0]?.start_time,
                        "HH:mm:ss",
                    ).valueOf();

                    let millisecondsElapsedTodayUntilEndTime: number = moment(
                        shift?.shift_historic[0]?.end_time,
                        "HH:mm:ss",
                    ).valueOf();

                    if (millisecondsElapsedTodayUntilStartTime > millisecondsElapsedTodayUntilEndTime) {
                        millisecondsElapsedTodayUntilEndTime += 24 * 60 * 60 * 1000;
                    }

                    const rangeStartTime = moment(millisecondsElapsedByDay + millisecondsElapsedTodayUntilStartTime);
                    const rangeEndTime = moment(millisecondsElapsedByDay + millisecondsElapsedTodayUntilEndTime);

                    const ranges = {
                        shiftId: shift?.shift_id,
                        startTime: shift?.shift_historic[0]?.start_time,
                        endTime: shift?.shift_historic[0]?.end_time,
                        rangeStartTime,
                        rangeEndTime,
                    };

                    return ranges;
                });

        const momentRanges =
            millisecondsElapsedUntilStartTimeAndEndTimeSinceSundayByDay &&
            millisecondsElapsedUntilStartTimeAndEndTimeSinceSundayByDay.map((millisecondsGroup) => {
                return {
                    dateRanges: momentRange.range(millisecondsGroup.rangeStartTime, millisecondsGroup.rangeEndTime),
                    shiftId: millisecondsGroup.shiftId as number,
                    startTime: millisecondsGroup.startTime,
                    endTime: millisecondsGroup.endTime,
                };
            });
        shiftsRanges = [...shiftsRanges, momentRanges];
    });

    let overlap = false;
    const shiftsIds: number[] = [];
    const shiftHoursByShiftId: { [key: number]: string[] }[] = [];
    const uniqueShiftIds = new Set<number>();
    for (let i = 0; i < shiftsRanges.length - 1; i++) {
        const currentRanges = shiftsRanges[i];
        for (let j = i + 1; j < shiftsRanges.length; j++) {
            const nextRanges = shiftsRanges[j];
            for (const currentRange of currentRanges) {
                for (const nextRange of nextRanges) {
                    if (currentRange.dateRanges.overlaps(nextRange.dateRanges)) {
                        overlap = true;
                        shiftsIds.push(currentRange.shiftId, nextRange.shiftId);
                        if (!uniqueShiftIds.has(currentRange.shiftId)) {
                            const currentRangeShiftTimePairs = {
                                [currentRange.shiftId as number]: [currentRange.startTime, currentRange.endTime],
                            };
                            shiftHoursByShiftId.push(currentRangeShiftTimePairs);
                            uniqueShiftIds.add(currentRange.shiftId);
                        }

                        if (!uniqueShiftIds.has(nextRange.shiftId)) {
                            const nextRangeShiftTimePairs = {
                                [nextRange.shiftId as number]: [nextRange.startTime, nextRange.endTime],
                            };
                            shiftHoursByShiftId.push(nextRangeShiftTimePairs);
                            uniqueShiftIds.add(nextRange.shiftId);
                        }
                    }
                }
            }
        }
    }

    const singleShiftsArray = Array.from(new Set(shiftsIds));

    return { singleShiftsArray, shiftHoursByShiftId, overlap };
};

export const areShiftDaysIdsEqual = (arr1: number[], arr2: number[]) => {
    return arr1?.length === arr2?.length && arr1?.every((value, index) => value === arr2[index]);
};

export const calculateShiftDate = (
    hours: number,
    minutes: number,
    seconds: number,
    shiftStart: boolean,
    millTimezone: string,
) => {
    // Get the current date and time
    const now = moment().tz(millTimezone);

    // Construct the date for yesterday
    const yesterday = moment().tz(millTimezone).subtract(1, "day");

    // Construct the date with the provided time
    const providedTime = moment().tz(millTimezone).set({
        hours: hours,
        minutes: minutes,
        seconds: seconds,
    });

    // Compare the provided time with the current time
    if (shiftStart) {
        if (providedTime.isAfter(now)) {
            // If the provided time is greater than now, use yesterday's date
            return providedTime.set({
                year: yesterday.year(),
                month: yesterday.month(),
                date: yesterday.date(),
            });
        } else {
            // If the provided time is equal to or less than now, use today's date
            return providedTime;
        }
    } else {
        if (providedTime.isBefore(now)) {
            // If the provided time is greater than now, use yesterday's date
            return providedTime.set({
                year: yesterday.year(),
                month: yesterday.month(),
                date: yesterday.date(),
            });
        } else {
            // If the provided time is equal to or less than now, use today's date
            return providedTime;
        }
    }
};

export const getCurrentShift = (
    shifts: {
        shift: IShift;
    }[],
    millTimezone: string,
    currentTime: string,
) =>
    shifts.find((shift) => {
        const today = moment().tz(millTimezone).format("dddd");
        const yesterday = moment().tz(millTimezone).subtract(1, "day").format("dddd");
        const now = moment(currentTime, "HH:mm:ss");

        const startTime = moment(shift.shift.shift_historic[0]?.start_time, "HH:mm:ss");
        const endTime = moment(shift.shift.shift_historic[0]?.end_time, "HH:mm:ss");
        const beginningOfDay = moment("00:00:00", "HH:mm:ss");

        //find if today is one of the days related to the shift
        const shiftEnabledToday = shift.shift.shift_historic[0]?.shift_days.find(
            (day) => day.day?.name === today && day.is_enabled,
        );

        //find if there is any shift from yesterday that carries on today
        const shiftCarryingOnFromYesterday = shifts.find(
            (shift) =>
                shift.shift.shift_historic[0]?.shift_days.find((day) => day.day?.name === yesterday) &&
                shift.shift.shift_historic[0]?.start_time > shift.shift.shift_historic[0]?.end_time,
        );

        if (shiftCarryingOnFromYesterday) {
            if (
                now > beginningOfDay &&
                now <= moment(shiftCarryingOnFromYesterday.shift.shift_historic[0]?.end_time, "HH:mm:ss")
            ) {
                return true;
            } else {
                if (shiftEnabledToday) {
                    // Check if the shift starts one day and finishes the following day
                    if (startTime.isAfter(endTime)) {
                        if (now.isSameOrAfter(startTime) || now.isBefore(endTime)) {
                            return true;
                        }
                    } else {
                        // Shift starts and ends on the same day
                        if (now.isBetween(startTime, endTime)) {
                            return true;
                        }
                    }
                }
            }
        } else {
            if (shiftEnabledToday) {
                // Check if the shift starts one day and finishes the following day
                if (startTime.isAfter(endTime)) {
                    if (now.isSameOrAfter(startTime) || now.isBefore(endTime)) {
                        return true;
                    }
                } else {
                    // Shift starts and ends on the same day
                    if (now.isBetween(startTime, endTime)) {
                        return true;
                    }
                }
            }
        }

        return false;
    });

export interface IShiftScheduleWindow {
    start_time: string; // "HH:mm:ss" in mill-local time
    end_time: string; // "HH:mm:ss" in mill-local time
    enabledDayNames: string[]; // English full day names, e.g. ["Monday", "Tuesday"]
}

/**
 * Determines whether a finalized downtime event belongs to the mill's
 * CURRENT or PREVIOUS shift occurrence. Such events must NOT be splittable from the
 * back office: recent events are still owned/reconciled by the HMIs and local-server,
 * so splitting them from the cloud risks synchronization conflicts.
 *
 * Reconstructs the recurring shift-occurrence start timestamps around `now` (in
 * mill-local time) from the event's own shift schedule, then treats the two most
 * recent occurrence starts as the previous + current shift. The event is considered
 * "recent" (→ not splittable) when it ended at/after the previous shift's start.
 *
 * Fail-safe: returns true (treat as recent → disable Split) whenever the boundary
 * can't be determined confidently (missing timezone/end date/schedule).
 */
export const isEventInCurrentOrPreviousShift = (
    shiftWindows: IShiftScheduleWindow[],
    eventEndDateUtc: string | Date | null | undefined,
    millTimezone: string | undefined,
    now: Moment = moment(),
): boolean => {
    if (!millTimezone || !eventEndDateUtc || !shiftWindows.length) return true;

    const reference = now.clone().tz(millTimezone);
    const eventEnd = moment.utc(eventEndDateUtc).tz(millTimezone);

    // Build candidate shift-occurrence start timestamps for the calendar days around now.
    const occurrenceStarts: number[] = [];
    for (let dayOffset = -3; dayOffset <= 1; dayOffset++) {
        const day = reference.clone().add(dayOffset, "day").startOf("day");
        const dayName = day.format("dddd");
        shiftWindows.forEach((window) => {
            if (!window.enabledDayNames.includes(dayName)) return;
            const [hours, minutes, seconds] = window.start_time.split(":").map(Number);
            occurrenceStarts.push(
                day
                    .clone()
                    .set({ hour: hours || 0, minute: minutes || 0, second: seconds || 0, millisecond: 0 })
                    .valueOf(),
            );
        });
    }

    const pastStarts = occurrenceStarts.filter((start) => start <= reference.valueOf()).sort((a, b) => a - b);
    // Need at least the current + previous occurrence to define the boundary.
    if (pastStarts.length < 2) return true;

    const previousShiftStart = pastStarts[pastStarts.length - 2];
    return eventEnd.valueOf() >= previousShiftStart;
};

export const formatCounter = (duration: number) => {
    const seconds = ("0" + Math.floor((duration / 1000) % 60)).slice(-2);
    const minutes = ("0" + Math.floor((duration / (1000 * 60)) % 60)).slice(-2);
    const hours = ("0" + Math.floor(duration / (1000 * 60 * 60))).slice(-2);

    return `${hours}:${minutes}:${seconds}`;
};

export const getRCAAPComplianceScoreByResponsible = (
    responsibleData: IRCAComplianceByResponsible | IAPComplianceByResponsible,
) => {
    let score =
        responsibleData.completed_on_time * ComplianceScore.COMPLETED_ON_TIME +
        responsibleData.pending * ComplianceScore.PENDING +
        responsibleData.completed_late * ComplianceScore.COMPLETED_LATE +
        responsibleData.pending_late * ComplianceScore.PENDING_LATE;

    return score;
};

const getObjectDeepCopy = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const getComplianceByType = (
    data: IRCAComplianceByResponsible[] | IAPComplianceByResponsible[],
    selectedComplianceType: string,
): IRCAComplianceByResponsible[] | IAPComplianceByResponsible[] => {
    type IRCAComplianceTypes = "completed_on_time" | "pending" | "completed_late" | "pending_late";

    // We get a deep copy of the original data object to avoid modifying it
    const deepCopyOfComplianceData = getObjectDeepCopy(data);
    if (selectedComplianceType === ComplianceTypes.NONE) {
        return data;
    } else {
        deepCopyOfComplianceData.forEach((dataByResponsible) =>
            Object.keys(dataByResponsible).forEach((dataItemName) => {
                if (
                    dataItemName !== selectedComplianceType &&
                    (Object.values(ComplianceTypes) as string[]).includes(dataItemName)
                ) {
                    dataByResponsible[dataItemName as IRCAComplianceTypes] = 0;
                }
            }),
        );

        return deepCopyOfComplianceData;
    }
};

export const removeFileExtension = (filename: string | undefined): any => {
    return filename?.replace(/\.[^.]+$/, "");
};

export const createTimeIntervalsOptions = () => {
    const intervals = [];
    let currentTime = moment("00:30:00", "HH:mm:ss");
    const endTime = moment("08:00:00", "HH:mm:ss");

    while (currentTime.isSameOrBefore(endTime)) {
        intervals.push(currentTime.format("HH:mm:ss"));
        currentTime.add(30, "minutes");
    }

    return intervals;
};

export const isNewFeatureEnabled = (millId: number) => {
    const featureConfig = process.env.REACT_APP_MILLS_IDS_FEATURE_ENABLED;

    if (!featureConfig || !millId) {
        return false;
    }

    try {
        const allowedIds = JSON.parse(featureConfig);

        if (!Array.isArray(allowedIds)) {
            return false;
        }

        if (allowedIds.includes("ALL")) {
            return true;
        }

        return allowedIds.some((id) => String(id) === String(millId));
    } catch {
        return false;
    }
};

/**
 * Compares specific fields between two objects to determine if they have the same values
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @param fieldsToValidate Array of field names to check
 * @returns True if all specified fields match, false otherwise
 */
export const objectComparator = (
    obj1: Record<string, any>,
    obj2: Record<string, any>,
    fieldsToValidate: string[],
): boolean => {
    try {
        for (const field of fieldsToValidate) {
            if (!obj1.hasOwnProperty(field) || !obj2.hasOwnProperty(field)) {
                return false;
            }

            if (Array.isArray(obj1[field]) && Array.isArray(obj2[field])) {
                if (obj1[field].length !== obj2[field].length) {
                    return false;
                }

                const sortedArr1 = [...obj1[field]].sort();
                const sortedArr2 = [...obj2[field]].sort();

                for (let i = 0; i < sortedArr1.length; i++) {
                    if (JSON.stringify(sortedArr1[i]) !== JSON.stringify(sortedArr2[i])) {
                        return false;
                    }
                }
            } else {
                if (obj1[field] !== obj2[field]) {
                    return false;
                }
            }
        }

        return true;
    } catch {
        return false;
    }
};

/**
 * Filters an object to only keep specified fields
 * @param obj The source object to filter
 * @param fieldsToKeep Array of field names to preserve in the result
 * @returns A new object with only the specified fields
 */
export const filterObjectProperties = <T extends Record<string, any>>(obj: T, fieldsToKeep: string[]): Partial<T> => {
    try {
        return fieldsToKeep.reduce((result, field) => {
            if (field in obj && obj[field] !== undefined) {
                result[field as keyof T] = obj[field];
            }
            return result;
        }, {} as Partial<T>);
    } catch {
        return {};
    }
};

/**
 * Validates that localserver data is not stale (>10 minutes old)
 * @param dataUpdatedTime The timestamp when the data was last updated (Date, or undefined)
 * @param maxAgeMinutes Maximum age in minutes before data is considered stale (default: 10)
 * @throws Error if data is stale, with message including the age in minutes
 */
export const validateLocalserverDataFreshness = (dataUpdatedTime?: Date, maxAgeMinutes: number = 10): void => {
    if (!dataUpdatedTime) {
        return;
    }

    const dataUpdatedDate = new Date(dataUpdatedTime);
    const now = Date.now();
    const timeDifference = now - dataUpdatedDate.getTime();
    const maxAgeInMs = maxAgeMinutes * 60 * 1000;

    if (timeDifference > maxAgeInMs) {
        const minutesDifference = Math.round(timeDifference / (60 * 1000));
        throw new Error(`Localserver data is stale (${minutesDifference} minutes old)`);
    }
};
